import { useState, useEffect, useCallback, type JSX } from 'react'
import {
  ClipboardList, RefreshCw, ArrowLeft, ArrowRight,
  Trash2, Plus, Pencil } from 'lucide-react'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useRbacApi } from '../../../components/rbac/useRbacApi.js'
import type { AuditLog } from '../../../components/rbac/types.js'
import { formatInTimezone } from '../../../utils/timezone.js'
import Select from '../../../components/ui/Select.js'
import DatePicker from '../../../components/ui/DatePicker.js'

const ACTION_OPTIONS = [
  { label: 'All Actions', value: '' },
  { label: 'Role Created', value: 'role_created' },
  { label: 'Role Updated', value: 'role_updated' },
  { label: 'Role Deleted', value: 'role_deleted' },
  { label: 'Permission Created', value: 'permission_created' },
  { label: 'Permission Updated', value: 'permission_updated' },
  { label: 'Permission Deleted', value: 'permission_deleted' },
  { label: 'Permission Granted', value: 'permission_granted' },
  { label: 'Permission Revoked', value: 'permission_revoked' },
]

function getActionBadge(action: string) {
  const a = action.toLowerCase()
  if (a.includes('deleted') || a.includes('revoked'))
    return { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' }
  if (a.includes('created') || a.includes('granted'))
    return { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' }
  if (a.includes('updated'))
    return { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' }
  return { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' }
}

function formatDate(iso: string) {
  return formatInTimezone(iso, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }, 'en-US')
}

function DiffViewer({ oldValue, newValue }: { oldValue: unknown; newValue: unknown }) {
  const [expanded, setExpanded] = useState(false)
  if (!oldValue && !newValue) return <span className="text-gray-300 text-xs">—</span>
  return (
    <div>
      <button onClick={() => setExpanded((v) => !v)} className="text-[10px] font-semibold text-[#3454cf] hover:underline">
        {expanded ? 'Hide diff' : 'View diff'}
      </button>
      {expanded && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          {oldValue !== undefined && oldValue !== null && (
            <div>
              <p className="text-red-500 font-medium mb-1">Before</p>
              <pre className="bg-red-50/50 border border-red-100 rounded-sm p-2 text-gray-700 overflow-auto max-h-32 text-[10px]">{JSON.stringify(oldValue, null, 2)}</pre>
            </div>
          )}
          {newValue !== undefined && newValue !== null && (
            <div>
              <p className="text-emerald-600 font-medium mb-1">After</p>
              <pre className="bg-emerald-50/50 border border-emerald-100 rounded-sm p-2 text-gray-700 overflow-auto max-h-32 text-[10px]">{JSON.stringify(newValue, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof ClipboardList
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}10` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: accent }} />
    </div>
  )
}

export default function PlatformAuditLogPage(): JSX.Element {
  const toast = useToast()
  const { listAuditLogs, loading } = useRbacApi({ baseUrl: '/platform/rbac' })

  const [logs, setLogs] = useState<AuditLog[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const limit = 20

  const [filters, setFilters] = useState({ action: '', from: '', to: '' })

  const loadLogs = useCallback(async () => {
    try {
      const params: Record<string, any> = { limit, offset: page * limit }
      if (filters.action) params.action = filters.action
      if (filters.from) params.from = filters.from
      if (filters.to) params.to = filters.to
      const data = await listAuditLogs(params)
      setLogs(data.logs)
      setTotal(data.total)
    } catch {
      toast.error('Error', 'Failed to load audit logs')
    }
  }, [page, filters, listAuditLogs])

  useEffect(() => { loadLogs() }, [loadLogs])

  const totalPages = Math.ceil(total / limit)

  const creates = logs.filter(l => l.action.includes('created')).length
  const updates = logs.filter(l => l.action.includes('updated')).length
  const deletes = logs.filter(l => l.action.includes('deleted') || l.action.includes('revoked')).length

  return (
    <div className="w-full space-y-5 px-6 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
            <ClipboardList className="h-5 w-5 text-[#3454cf]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">RBAC Audit Logs</h1>
            <p className="text-sm text-gray-400 mt-0.5">Full history of role and permission changes.</p>
          </div>
        </div>
        <button
          onClick={() => { setPage(0); loadLogs() }}
          disabled={loading}
          className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Total Entries" value={total} icon={ClipboardList} accent="#3454cf" />
        <StatCard label="Creations" value={creates} icon={Plus} accent="#10b981" />
        <StatCard label="Updates" value={updates} icon={Pencil} accent="#f6931c" />
        <StatCard label="Deletions" value={deletes} icon={Trash2} accent="#ef4444" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</label>
            <Select
              ariaLabel="Action filter"
              value={filters.action}
              onChange={(v) => { setFilters({ ...filters, action: v }); setPage(0) }}
              options={ACTION_OPTIONS}
              className="min-w-[180px]"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">From</label>
                        <DatePicker
              ariaLabel="From"
              value={filters.from}
              onChange={(v) => { setFilters({ ...filters, from: v }); setPage(0) }}
              className="h-10"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">To</label>
                        <DatePicker
              ariaLabel="To"
              value={filters.to}
              onChange={(v) => { setFilters({ ...filters, to: v }); setPage(0) }}
              className="h-10"
            />
          </div>
          {(filters.action || filters.from || filters.to) && (
            <button
              onClick={() => { setFilters({ action: '', from: '', to: '' }); setPage(0) }}
              className="h-10 px-3 text-xs font-semibold text-gray-500 hover:text-gray-700 border border-gray-200 rounded-sm hover:bg-gray-50 transition-all"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 text-sm text-gray-400">
        <span>Showing <strong className="text-gray-800">{logs.length}</strong> of <strong className="text-gray-800">{total}</strong> entries</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-sm border border-gray-100 overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <i className="pi pi-spin pi-spinner text-[#3454cf] text-2xl" />
          </div>
        )}
        {!loading && logs.length === 0 && (
          <div className="text-center py-16 text-gray-300">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No audit log entries found</p>
          </div>
        )}
        {!loading && logs.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Timestamp</th>
                <th className="text-left px-5 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Actor</th>
                <th className="text-left px-5 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Action</th>
                <th className="text-left px-5 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Resource</th>
                <th className="text-left px-5 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Changes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.map((log) => {
                const badge = getActionBadge(log.action)
                return (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4 text-gray-500 whitespace-nowrap text-xs">{formatDate(log.createdAt)}</td>
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-gray-800">{(log as any).actorName || 'Unknown'}</div>
                      <div className="text-[10px] text-gray-300 font-mono truncate max-w-[120px]">{log.actorId}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${badge.bg} ${badge.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${badge.dot}`} />
                        {log.action.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-xs">
                        <span className="font-medium text-gray-700 capitalize">{log.resourceType}</span>
                        {log.resourceId && <span className="block text-gray-300 font-mono truncate max-w-[120px] text-[10px]">{log.resourceId}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <DiffViewer oldValue={log.oldValue} newValue={log.newValue} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const p = totalPages <= 7 ? i : page < 4 ? i : page > totalPages - 4 ? totalPages - 7 + i : page - 3 + i
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 text-sm rounded-sm font-medium transition-all ${
                    p === page ? 'bg-[#3454cf] text-white' : 'text-gray-500 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                  }`}
                >
                  {p + 1}
                </button>
              )
            })}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Next <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
